// runHistory — P-001: client-wrapper för `list_my_recent_runs`-RPC.
// Används av framtida UI-dashboard som visar run-status + re-run-knapp.
//
// RLS + trigger-gate:
// - RPC är SECURITY DEFINER men filter via workflows.created_by=auth.uid()
//   så users ser bara egna runs.
// - Non-enterprise-users får 0 rows (tabellen är tom för dem via insert-
//   trigger som kastar enterprise-required-error vid insert).

import { callRpc } from '@/lib/supabaseTyped';

export type RunStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'cancelled';
export type TriggerKind = 'manual' | 'webhook' | 'scheduled' | 'api' | 'test';

export interface RunRow {
  id: string;
  workflow_id: string;
  status: RunStatus;
  trigger_kind: TriggerKind;
  started_at: string;
  ended_at: string | null;
  error_message: string | null;
}

export interface ListRunsResult {
  success: boolean;
  runs: RunRow[];
  error?: string;
}

export async function listMyRecentRuns(
  workflowId?: string,
  limit = 50,
): Promise<ListRunsResult> {
  const { data, error } = await callRpc<RunRow[]>(
    'list_my_recent_runs',
    {
      p_workflow_id: workflowId ?? null,
      p_limit: limit,
    },
  );
  if (error) {
    return { success: false, runs: [], error: error.message };
  }
  return { success: true, runs: data ?? [] };
}

/** D1: Hämta trigger_data från en failed run för att re-trigga via
 *  run-workflow edge-fn. Owner-only (coop-members kan bara se, inte
 *  re-trigga). Klient anropar triggerWorkflowRun med resultatet. */
export interface RetryFetchResult {
  success: boolean;
  workflow_id?: string;
  trigger_data?: unknown;
  error?: string;
}

export async function getFailedRunForRetry(runId: string): Promise<RetryFetchResult> {
  const { data, error } = await callRpc<RetryFetchResult>(
    'get_failed_run_for_retry',
    { p_run_id: runId },
  );
  if (error) return { success: false, error: error.message };
  return data ?? { success: false, error: 'empty-response' };
}
